import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { RowActions } from "./ui/RowActions";
import { workerCategoryOf, type WorkerCategory } from "../workerCategory";
import { ColumnFilterHead, useColumnFilterMenu } from "./ColumnFilterHead";
import type { BootstrapResponse, EmploymentContractType, Robot, Worker, WorkerGroup } from "../types";
import type { RobotControls } from "./RobotManagement";
import { workerGroupLabels, type WorkerControls } from "./WorkerManagement";
import { WorkerWeekStrip, type WeekSchedule } from "./WorkerWeekStrip";
import { useWorkerStatusRows } from "./useWorkerStatusRows";
import { EfficiencyEditor } from "./EfficiencyEditor";
import { ChevronLeft } from "lucide-react";
import { Button } from "./ui/Button";
import { Checkbox } from "./ui/Checkbox";
import { Modal } from "./ui/Modal";
import { InfoIcon } from "./ui/InfoIcon";
import { EmptyState, Panel } from "./ui/Surface";
import { DataTable, DataTableFilters, DataTablePagination, useDataTable, DataTableContent, type ColumnDef } from "./easyui/AdvancedDataTable";
import "./easyui/easyui.css";
import "./DataTableSkin.css";

const time = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const money = (value: number) => `${value.toLocaleString("ko-KR")}원`;

/** Weekday/weekend can run different lengths from the same start time; only show two ranges when they actually differ. */
/* 평일과 주말을 한 줄에 이어 붙이면 열이 좁을 때 아무 데서나 접혀 "주말"과 그 시각이
   갈라진다. 처음부터 두 줄로 내려, 접히는 자리를 뜻이 있는 곳으로 정해 둔다. */
/* 저장된 분은 휴게를 뺀 실근무다. 퇴근 시각을 말하려면 휴게를 다시 더해야 한다 —
   안 더하면 09:00–18:00으로 적은 사람이 표에서는 09:00–17:00으로 나온다. */

/* 구분을 여기서 다시 계산하지 않는다. workerCategory.ts가 "모든 화면에서 한 규칙"이라고
   적어 두고도 이 표만 제 계산을 들고 있어, 계약 없는 분들이 정규직으로 찍히고 있었다. */
/* 2026-10-19 밤 지윤: 구분은 이제 셋뿐이다 — 로봇 · 사람 · 업무위탁. 등록 창의 구분과 같은 말을 쓴다.
   정규직·기간제·도급제·단기알바는 계약이 따로 들고 있고, 표에서는 모두 "사람"이다. */
type Category = "robot" | "human" | "freelance";
const toCategory = (category: WorkerCategory): Category => category === "robot" ? "robot" : category === "freelance" ? "freelance" : "human";
const categoryLabels: Record<Category, string> = { robot: "로봇", human: "사람", freelance: "업무위탁" };
const categoryOrder: Category[] = ["robot", "human", "freelance"];

type Row = {
  id: string;
  kind: "human" | "robot";
  category: Category;
  name: string;
  output: string;
  workGroups: WorkerGroup[];
  schedule: WeekSchedule;
  priority: number;
  active: boolean;
  robot?: Robot;
  worker?: Worker;
};

export type RosterStatus = "all" | "active" | "inactive";

export function WorkerRosterSummary({ data, rows }: { data: BootstrapResponse; rows?: { kind: "human" | "robot" }[] }) {
  /* 걸러 놓고도 전체 수를 적으면 화면과 어긋난다 — 로봇만 골라 세 줄만 남았는데 "사람 30"이
     서 있으면 그 숫자가 무엇을 센 것인지 알 수 없다. */
  if (rows) return <span className="worker-count">
    <strong>로봇 {rows.filter(row => row.kind === "robot").length} · 사람 {rows.filter(row => row.kind === "human").length}</strong>
  </span>;
  /* 세는 말만 남긴다. 상태를 고르는 일은 표의 "상태" 머리글이 맡는다 — 다른 거르개가 모두
     머리글에 있는데 이것만 표 밖에 떠 있으면 같은 일에 규칙이 둘이 된다. */
  return <span className="worker-count">
    <strong>로봇 {(data.robots ?? []).length} · 사람 {data.workers.filter((worker) => worker.kind === "human").length}</strong>
  </span>;
}

/* 이 둘은 반드시 모듈 자리에 선다 — 컴포넌트 안에서 만들면 그릴 때마다 "다른 부품"이
   되어 React가 머리줄을 떼었다 다시 붙인다. 그 순간 찾기 칸이 커서를 잃어, 한 글자 치면
   더는 안 쳐진다(정산 묶음명 칸에서 같은 일이 났다). */
/* 머리줄은 정산 장부와 같은 꼴이다 — 왼쪽에 하는 일, 오른쪽에 찾기. 받아 온 표의
   마크업을 그대로 베꼈다. */
function RosterHead<T>({ onCompose, onRegister }: { onCompose: () => void; onRegister: () => void }) {
  const { searchTerm, setSearchTerm, selectedRowIds } = useDataTable<T>();
  return (
    <div className="payroll-records-head border-b border-border/50 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2.5 sm:gap-4 select-none">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
        {/* 2026-10-19 밤 지윤: 정산과 같은 위계 — 등록은 + 아이콘(32×32 회색), 이 화면의 큰 일인 공지 메시지가 검정 CTA.
            단추 글자는 늘 「공지 메시지」, 고른 사람이 없으면 꺼져 있다. */}
        <Button className="worker-register-action" variant="secondary" size="compact" aria-label="작업자 등록" title="작업자 등록" onClick={onRegister}>
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M10 4.5v11M4.5 10h11" /></svg>
        </Button>
        <Button className="worker-compose-action" variant="secondary" size="compact" disabled={!selectedRowIds.size} onClick={onCompose}
          aria-label={selectedRowIds.size ? `공지 메시지 (${selectedRowIds.size}명)` : "공지 메시지"}>
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"><path d="M3.5 5.5a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H8l-3.5 3v-3h0a1 1 0 0 1-1-1z" /></svg>
          <span>공지 메시지</span>
        </Button>
      </div>
      <div className="flex items-center gap-1.5 sm:gap-2.5 w-full sm:w-auto">
        <div className="relative flex-1 sm:w-64 min-w-0">
          <svg viewBox="0 0 24 24" aria-hidden="true" className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input type="text" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder="검색" aria-label="찾기"
            className="w-full pl-9 pr-8 py-1.5 rounded-lg border border-border/60 bg-surface-raised/20 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-border transition-colors font-sans" />
        </div>
      </div>
    </div>
  );
}

/* 2026-10-19 밤 지윤: 정산의 「1건 · 3.3% 공제 후 …」 줄과 같은 자리·같은 글자로 고른 사람 수를 띄운다.
   고른 사람이 없으면 이 줄이 할 일을 말한다(단추 글자는 늘 「공지 메시지」). */
function RosterSelectionLine<T>() {
  const { selectedRowIds } = useDataTable<T>();
  const count = selectedRowIds.size;
  /* 고른 사람이 없으면 줄을 두지 않는다 — 안내 문구(「사람을 선택하면 공지 메시지를…」)는 걷었다, 고르면 그때 몇 명인지만 선다(2026-10-21 지윤). */
  if (!count) return null;
  return <div className="payroll-selection-bar worker-selection-bar">
    <span className="payroll-selection-count" aria-live="polite">{`${count.toLocaleString()}명`}</span>
  </div>;
}

/* 부품이 고른 줄을 우리 쪽(공지 수신자)으로 옮긴다 — 부품은 고른 값을 밖으로 넘겨주는
   길이 없어서, 아무것도 안 그리는 자식 하나가 context로 읽어 전한다. */
function RosterSelectionBridge<T>({ messageSelection, onToggleMessageRecipient }: { messageSelection: ReadonlySet<string>; onToggleMessageRecipient: (id: string) => void }) {
  const { selectedRowIds } = useDataTable<T>();
  const key = [...selectedRowIds].join(",");
  useEffect(() => {
    const next = new Set(selectedRowIds);
    for (const id of next) if (!messageSelection.has(id)) onToggleMessageRecipient(id);
    for (const id of messageSelection) if (!next.has(id)) onToggleMessageRecipient(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
}

/** Unified operational roster (people + robots) — same shape as the old robot table. */
export function WorkerScheduleSummary({ data, refresh, actorName, robotControls, workerControls, messageSelection, onToggleMessageRecipient, onCompose, onExit }: {
  data: BootstrapResponse; refresh: () => Promise<void>; actorName: string; robotControls: RobotControls | null; workerControls: WorkerControls | null;
  messageSelection: ReadonlySet<string>; onToggleMessageRecipient: (id: string) => void; onCompose: () => void; onExit?: () => void;
}) {
  const [efficiencyOpen, setEfficiencyOpen] = useState(false);
  const [efficiencyProjectId, setEfficiencyProjectId] = useState("");
  const efficiencyDirty = useRef(false);
  const efficiencyProject = data.projects.find((project) => project.id === efficiencyProjectId)
    ?? data.projects.find((project) => project.status === "active" && project.subtasks.some((task) => task.stage === "sewing"))
    ?? data.projects.find((project) => project.subtasks.some((task) => task.stage === "sewing")) ?? data.projects[0];
  const closeEfficiency = () => {
    if (efficiencyDirty.current && !window.confirm("저장하지 않은 효율 수정이 있습니다. 닫으시겠습니까?")) return;
    setEfficiencyOpen(false);
  };
  const statusById = useWorkerStatusRows(data);
  const categoryOf = useMemo(() => workerCategoryOf(data), [data]);
  const rows = useMemo<Row[]>(() => {
    const workers: Row[] = data.workers.filter((worker) => worker.kind === "human").map((worker): Row => ({
      id: worker.id, kind: "human", // No contract on file is missing paperwork, not a separate kind of employment.
      category: toCategory(categoryOf(worker)),
      name: worker.displayedName, output: worker.grade || "—",
      workGroups: worker.workGroups,
      active: worker.active,
      /* 단기알바도 적은 대로 보여 준다. 예전에는 기본값이 사실처럼 찍히는 게 문제라 비웠는데,
         이제 등록 화면에서 사람마다 직접 받으므로 적은 것이 안 보이는 쪽이 더 나쁘다 —
         입력한 사람은 저장이 안 된 줄 안다. 그날 실제로 몇 시간 일했는지는 근무기록이 안다. */
      priority: worker.planningPriority ?? 0,
      schedule: { workDays: worker.workDays, workStartMinutes: worker.workStartMinutes, weekdayMinutes: worker.weekdayMinutes, weekendMinutes: worker.weekendMinutes, breakMinutes: worker.breakMinutes },
      worker
    })).sort((left, right) => Number(right.active) - Number(left.active) || left.name.localeCompare(right.name, "ko-KR"));
    const robots: Row[] = (data.robots ?? []).map((robot) => ({
      id: robot.workerId, kind: "robot", category: "robot", name: robot.displayedName, output: "A",
      workGroups: robot.workGroups,
      active: robot.active,
      priority: robot.planningPriority ?? 0,
      schedule: { workDays: robot.workDays, workStartMinutes: robot.workStartMinutes, weekdayMinutes: robot.weekdayMinutes, weekendMinutes: robot.weekendMinutes },
      robot
    }));
    // Robots lead the roster, matching the planner board and the dispatch dropdown.
    return [...robots, ...workers];
  }, [data.workers, data.robots, categoryOf]);
  const [category, setCategory] = useState<Category | "all">("all");
  const [nameFilter, setNameFilter] = useState<string | undefined>();
  const [grade, setGrade] = useState<string | undefined>();
  const [group, setGroup] = useState<string | undefined>();
  const [status, setStatus] = useState<RosterStatus>("all");
  /* 등록 창은 사람에게 "계약 유지/종료", 로봇에게 "운용 유지/종료"를 묻는다. 표는 그 뒷말만
     쓴다 — 앞말을 살리면 로봇 줄과 사람 줄이 다른 낱말을 쓰게 되고, 거르개는 둘 중 하나만
     고를 수 있어 한쪽이 빠진다. */
  const statusOptions = [{ value: "active", label: "유지" }, { value: "inactive", label: "만료" }];
  const menu = useColumnFilterMenu();
  /* 등급의 근거는 눌러서 연다. 열로 세우면 서른 줄 내내 자리를 차지하는데 매번 훑을 값이
     아니고, 그 둘이 궁금해지는 순간은 등급을 보다가이기 때문이다. */
  const [gradeOpen, setGradeOpen] = useState<string | null>(null);
  useEffect(() => {
    if (!gradeOpen) return;
    const dismiss = (event: MouseEvent) => { if (!(event.target as HTMLElement).closest(".worker-grade")) setGradeOpen(null); };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [gradeOpen]);
  /* 고를 수 있는 값은 고르면 뭔가 나오는 값이어야 한다 — 다른 열의 조건을 이미 거친 목록에서
     뽑으면, 로봇만 보는 중에는 등급 목록도 로봇 것만 남는다. */
  const filtered = useMemo(() => rows.filter((row) =>
    (category === "all" || row.category === category) && (!nameFilter || row.name === nameFilter) && (!grade || row.output === grade)
    && (!group || row.workGroups.includes(group as WorkerGroup))
    && (status === "all" || row.active === (status === "active"))),
    [rows, category, nameFilter, grade, group, status]);
  /* 공정이 흐르는 순서대로 세운다 — 밑 → 재봉 → 완성 → QC → 운영. 이 표에 실제로 있는
     작업군만 남긴다: 아무도 안 맡은 공정이 목록에 서 있으면 골라 봐야 빈 표가 된다. */
  const groupOptions = useMemo(() => (Object.keys(workerGroupLabels) as WorkerGroup[])
    .filter(value => rows.some(row => row.workGroups.includes(value)))
    .map(value => ({ value, label: workerGroupLabels[value] })), [rows]);
  const nameOptions = useMemo(() => rows.filter(row => (category === "all" || row.category === category) && (!grade || row.output === grade))
    .map(row => ({ value: row.name, label: row.name })), [rows, category, grade]);
  const categoryOptions = useMemo(() => categoryOrder.filter(value => rows.some(row => row.category === value))
    .map(value => ({ value, label: categoryLabels[value] })), [rows]);
  const gradeOptions = useMemo(() => [...new Set(rows.filter(row => category === "all" || row.category === category).map(row => row.output))]
    .sort().map(value => ({ value, label: value })), [rows, category]);
  const filteredIds = filtered.map((row) => row.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => messageSelection.has(id));
  const toggleAllSelected = () => filteredIds.forEach((id) => { if (allSelected === messageSelection.has(id)) onToggleMessageRecipient(id); });

  const rosterColumns: ColumnDef<typeof rows[number]>[] = [
    /* 고르기 칸은 부품 것을 쓴다 — 우리 것을 더하면 체크 상자가 한 줄에 둘이 선다.
       고른 값은 아래 다리가 우리 쪽으로 옮긴다. */
    /* 2026-10-19 밤 지윤: 구분이 이름보다 앞, 근무·상태는 이름 바로 뒤 — 등록 창이 구분부터 읽히는 순서와 맞춘다. */
    { id: "category", header: "구분", sortable: true, filterable: true, priority: "high",
      accessorFn: row => categoryLabels[row.category], cell: ({ value }) => String(value) },
    { id: "name", header: "이름", sortable: true, priority: "high", accessorKey: "name", cell: ({ value }) => String(value) },
    { id: "schedule", header: "근무", priority: "medium", accessorFn: () => "",
      cell: ({ row }) => <WorkerWeekStrip schedule={row.schedule} /> },
    { id: "status", header: "상태", sortable: true, filterable: true, priority: "high",
      accessorFn: row => row.active ? "유지" : "만료", cell: ({ value }) => String(value) },
    { id: "groups", header: "작업군", sortable: true, filterable: true, priority: "medium",
      accessorFn: row => row.workGroups.map(value => workerGroupLabels[value]).join(" · ") || "—", cell: ({ value }) => String(value) },
    /* 등급 옆 ⓘ = 단위 작업 효율표(등급이 어떻게 산정됐는지). 10/12 표를 바꾸며 빠졌던 것을 되살린다(2026-10-19 밤 지윤). */
    { id: "output", header: "등급", sortable: true, priority: "medium",
      headerExtra: <button type="button" className="worker-efficiency-info" aria-label="단위 작업 효율표 보기" title="단위 작업 효율표"
        onClick={event => { event.stopPropagation(); setEfficiencyOpen(true); }}><InfoIcon /></button>,
      accessorFn: row => row.output, cell: ({ value }) => String(value) },
    { id: "priority", header: "우선순위", sortable: true, priority: "low",
      accessorFn: row => row.priority, cell: ({ value }) => Number(value) > 0 ? String(value) : "—" },
    /* 개인정보는 열 하나에 ⓘ 로만 선다 — 연락처·주소·계좌·주민번호·신분증을 표에 펼치면
       남의 어깨 너머로 다 읽히고, 열 다섯 개짜리 표가 된다. 누른 자리 옆에 떠서 누구 것인지
       보이고, 바깥을 누르면 닫힌다. 로봇 줄에는 설 자리가 없다. */
    { id: "private", header: "개인정보", priority: "low", accessorFn: () => "",
      cell: ({ row }) => row.kind === "human" && row.worker && workerControls
        ? <span onClick={event => event.stopPropagation()}>
            <Button variant="quiet" size="icon" aria-label={`${row.name} 개인정보 보기`} title="개인정보"
              onClick={event => workerControls.openPrivateInfo(row.worker!, event.currentTarget.getBoundingClientRect())}><InfoIcon /></Button>
          </span>
        : null },
    { id: "manage", header: "관리", priority: "low", accessorFn: () => "",
      /* 2026-10-19 지윤 지시: 줄 끝 손잡이는 저쪽 설정 창 표처럼 ⋮ 하나다. 단추 둘을 늘어놓으면
         그 폭을 줄마다 비워 둬야 하고, 통창(1024)에서 먼저 찌그러진다. */
      cell: ({ row }) => <div className="robot-row-actions" onClick={event => event.stopPropagation()}>
        {row.kind === "human" && row.worker && workerControls && <RowActions label={row.worker.displayedName} actions={[
          { label: "수정", onSelect: () => workerControls.openWorker(row.worker!) },
          { label: "삭제", onSelect: () => workerControls.deleteWorker(row.worker!), danger: true },
        ]} />}
        {row.kind === "robot" && row.robot && robotControls && <RowActions label={row.robot.displayedName} actions={[
          { label: "수정", onSelect: () => { const worker = data.workers.find(item => item.id === row.id); if (worker) workerControls?.openWorker(worker); } },
          { label: "삭제", onSelect: () => robotControls.deleteRobot(row.robot!), danger: true },
        ]} />}
      </div> },
  ];

  return (
    <div className="worker-roster-group">
    <Panel className="worker-schedule-summary">
      {/* Same header slot and spacing as the project card on the work tab. */}
      {/* 수신자는 표에서 고르고, 보내는 버튼은 표 끝에 있었다. 작업자가 서른 명이 넘으면
          고른 뒤 버튼까지 스크롤해야 한다. 고르는 자리와 보내는 자리를 같은 높이에 둔다. */}
      {/* 표는 장부·정산과 같은 것을 쓴다(easyui, MIT) — 셋 다 "줄을 찾아 읽는" 같은 일이라
          서로 다른 표로 서면 같은 손놀림을 세 번 배워야 한다. 거르개도 열에 딸려 온다. */}
      <div className="planner-records-table worker-roster-table">
        {/* 쪽을 나누지 않는다 — 작업자 목록은 한 번에 훑어보는 명부라, 스무 줄에서 끊기면
            서른한 명 중 누가 빠졌는지 보려고 쪽을 넘겨야 한다.
            그래도 맨 아래 쪽 넘김 줄(「1/1」)은 둔다 — 정산 표와 같은 끝 표시가 없어 「끝까지 스크롤이 안 되나」로 읽혔다(2026-10-21 지윤). */}
        <DataTable data={filtered} columns={rosterColumns} defaultPageSize={1000} getRowId={row => row.id}>
          <RosterHead onCompose={onCompose} onRegister={() => workerControls?.openWorker()} />
          <RosterSelectionBridge messageSelection={messageSelection} onToggleMessageRecipient={onToggleMessageRecipient} />
          <DataTableFilters />
          <RosterSelectionLine />
          <DataTableContent emptyTitle={rows.length ? "검색 결과가 없습니다." : "등록된 작업자·로봇이 없습니다."} emptyDescription="" />
          <DataTablePagination />
        </DataTable>
      </div>
      {!filtered.length && <EmptyState>{rows.length ? "검색 조건에 맞는 항목이 없습니다." : "등록된 작업자·로봇이 없습니다."}</EmptyState>}
      {efficiencyOpen && <Modal onBack={onExit ? closeEfficiency : undefined} title="단위 작업 효율표" className="efficiency-modal" onClose={() => { closeEfficiency(); onExit?.(); }}>
        {/* 설정 창 안에서는 제목이 내용 첫 줄(‹ + 15/20 580) — 다른 한 단계 들어간 창과 같은 자리. */}
        <div className="worker-profile-page-title">
          {onExit && <button type="button" className="worker-profile-page-back" aria-label="뒤로" onClick={closeEfficiency}><ChevronLeft size={20} aria-hidden="true" /></button>}
          <span aria-hidden="true">단위 작업 효율표</span>
        </div>
        {efficiencyProject
          ? <EfficiencyEditor data={data} project={efficiencyProject} refresh={refresh} actorName={actorName}
              onProjectChange={setEfficiencyProjectId} hasUnsavedChanges={(value) => { efficiencyDirty.current = value; }} />
          : <EmptyState>등록된 프로젝트가 없습니다.</EmptyState>}
      </Modal>}
    </Panel>
    </div>
  );
}
